import dayjs from 'dayjs';
import { Field, useFormikContext } from 'formik';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePhoneOptionsContext } from '../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../hooks/useAxiosHook';
import { isStreetFreeInputAllowed } from '../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../utils/constants/phoneNumbers';
import {
  placeItemTemplate,
  placeValueTemplate,
} from '../../../../utils/helpers/misc';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../utils/helpers/phoneNumbers';
import FieldWithErrors from '../../../Forms/FieldWithErrors/FieldWithErrors';

function CreateEditDialogBasicInfoTab({ isEditDialog }) {
  const { t } = useTranslation();

  const { values, errors, setFieldValue } = useFormikContext();
  console.log('errors ', errors);
  console.log('values ', values);
  const { data: municipalities, isLoading: isLoadingMunicipalities } =
    useAxiosHook('/municipalities?limit=0');

  const {
    data: places,
    reload: reloadPlaces,
    isLoading: isLoadingPlaces,
  } = useAxiosHook(
    `/municipalities/${values.address.municipalityId}/places?limit=0`,
    {
      skipWhen: !values?.address.municipalityId,
      skipCache: true,
    }
  );

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isLoadingStreets,
  } = useAxiosHook(`/places/${values?.address.placeId}/streets?limit=0`, {
    skipWhen: !values?.address.placeId,
    skipCache: true,
  });

  const municipalitiesOptions = useMemo(
    () =>
      municipalities && municipalities?.data?.length > 0
        ? municipalities?.data?.map((m) => ({
            label: m.name,
            value: m.id,
          }))
        : [],
    [municipalities]
  );

  const placesOptions = useMemo(
    () =>
      places && places?.length > 0
        ? places?.map((m) => ({
            label: m.place_name,
            value: m.place_id,
          }))
        : [],
    [places]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets]
  );

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  return (
    <div className="basic-info-tab p-fluid">
      <div>
        <FieldWithErrors name="ime" label={t('Name')}>
          <Field
            as={InputText}
            id="ime"
            name="ime"
            maxLength="25"
            data-cy="name"
          />
        </FieldWithErrors>

        <FieldWithErrors name="prezime" label={t('Surname')}>
          <Field
            as={InputText}
            id="prezime"
            name="prezime"
            maxLength="25"
            data-cy="surname"
          />
        </FieldWithErrors>

        {isEditDialog && (
          <FieldWithErrors name="username" label={t('Email (username)')}>
            <Field
              as={InputText}
              name="username"
              id="username"
              maxLength="254"
              data-cy="username"
            />
          </FieldWithErrors>
        )}

        <FieldWithErrors
          name="address.municipalityId"
          label={t('Municipality')}
        >
          <Field
            name="address.municipalityId"
            as={Dropdown}
            inputId="address.municipalityId"
            options={municipalitiesOptions}
            filter
            filterPlaceholder={t('Search')}
            disabled={isLoadingMunicipalities}
            placeholder={isLoadingMunicipalities ? t('Loading...') : null}
            onBlur={() =>
              setFieldValue(
                'address.municipalityId',
                values.address.municipalityId
              )
            }
            onChange={(e) => {
              const item = municipalitiesOptions?.find(
                (s) => s.value === e.value
              );

              setFieldValue('address.municipalityId', item.value);
              setFieldValue('address.municipalityName', item?.label);

              reloadPlaces({ url: `municipalities/${e.value}/places` });

              setFieldValue('address.placeId', null);
              setFieldValue('address.placeName', '');
              setFieldValue('address.streetId', null);
              setFieldValue('address.streetName', '');
              setFieldValue('address.housenumber', '');
              setFieldValue('address.entrance', '');
              setFieldValue('address.apartment', '');
            }}
            className="data-cy-municipality"
          />
        </FieldWithErrors>

        <FieldWithErrors name="address.placeId" label={t('Place')}>
          <Field
            name="address.placeId"
            inputId="address.placeId"
            as={Dropdown}
            options={placesOptions}
            itemTemplate={placeItemTemplate}
            valueTemplate={placeValueTemplate}
            disabled={!values.address.municipalityId || isLoadingPlaces}
            filter
            filterPlaceholder={t('Search')}
            placeholder={isLoadingPlaces ? t('Loading...') : null}
            onBlur={() =>
              setFieldValue('address.placeId', values.address.placeId)
            }
            onChange={(e) => {
              if (e.value) {
                const place = placesOptions.find((p) => p.value === e.value);

                reloadStreets({
                  url: `places/${e.value}/streets?limit=0`,
                });

                setFieldValue('address.placeId', place.value);
                setFieldValue('address.placeName', place.label);
              } else {
                setFieldValue('address.placeId', null);
                setFieldValue('address.placeName', '');
                setFieldValue('address.streetId', null);
                setFieldValue('address.streetName', '');
                setFieldValue('address.housenumber', '');
                setFieldValue('address.entrance', '');
                setFieldValue('address.apartment', '');
              }
            }}
          />
        </FieldWithErrors>

        {isStreetFreeInputAllowed ? (
          <FieldWithErrors name="adresa" label={t('Address')}>
            <Field
              as={InputText}
              id="adresa"
              name="adresa"
              maxLength="50"
              data-cy="address"
            />
          </FieldWithErrors>
        ) : (
          <>
            <FieldWithErrors name="address.streetId" label={t('Street')}>
              <Field
                disabled={!values.address.placeId || isLoadingStreets}
                name="address.streetId"
                inputId="address.streetId"
                as={Dropdown}
                options={streetsOptions}
                filter
                filterPlaceholder={t('Search')}
                placeholder={isLoadingStreets ? t('Loading...') : null}
                onBlur={() =>
                  setFieldValue('address.streetId', values.address.streetId)
                }
                onChange={(e) => {
                  if (e.value) {
                    const item = streetsOptions?.find(
                      (s) => s.value === e.value
                    );

                    setFieldValue('address.streetId', item.value);
                    setFieldValue('address.streetName', item.label);
                  } else {
                    setFieldValue('address.streetId', null);
                    setFieldValue('address.streetName', '');
                    setFieldValue('address.housenumber', '');
                    setFieldValue('address.entrance', '');
                    setFieldValue('address.apartment', '');
                  }
                }}
              />
            </FieldWithErrors>

            <div className="street-numbers">
              <FieldWithErrors
                name="address.housenumber"
                label={t('Street No.')}
              >
                <Field
                  as={InputText}
                  id="address.housenumber"
                  name="address.housenumber"
                  maxLength="254"
                  data-cy="address.housenumber"
                  disabled={!values.address.streetId}
                />
              </FieldWithErrors>

              <span className="divider">/</span>

              <FieldWithErrors
                name="address.entrance"
                label={t('Entrance No.')}
              >
                <Field
                  as={InputText}
                  id="address.entrance"
                  name="address.entrance"
                  maxLength="254"
                  data-cy="address.entrance"
                  disabled={!values.address.streetId}
                />
              </FieldWithErrors>

              <span className="divider">/</span>
              <FieldWithErrors name="address.apartment" label={t('Flat No.')}>
                <Field
                  as={InputText}
                  id="address.apartment"
                  name="address.apartment"
                  maxLength="254"
                  data-cy="address.apartment"
                  disabled={!values.address.streetId}
                />
              </FieldWithErrors>
            </div>
          </>
        )}

        <FieldWithErrors name="telefon" label={t('Phone')}>
          <div className="p-inputgroup number-group">
            <Field
              as={Dropdown}
              className="telefon_region"
              style={{ padding: 0, width: '5rem' }}
              id="telefon_region"
              name="telefon_region"
              options={languages}
              itemTemplate={countryOptionTemplate}
              valueTemplate={selectedCountryTemplate}
              data-cy="telefon_region"
            />

            <Field
              as={InputText}
              id="telefon"
              name="telefon"
              maxLength="13"
              value={formatPhoneNumber(
                values.telefon,
                values.telefon_region ||
                  getPhoneorMobileNumberRegion(values.telefon)
              )}
              onChange={(e) => {
                setFieldValue(
                  'telefon',
                  e.target.value?.replace(invalidPhoneNumberCharactersRegex, '')
                );
              }}
              data-cy="phone"
            />
          </div>
        </FieldWithErrors>

        <FieldWithErrors name="mobilen" label={t('Mobile')}>
          <div className="p-inputgroup number-group">
            <Field
              as={Dropdown}
              className="mobilen_region"
              style={{ padding: 0, width: '5rem' }}
              id="mobilen_region"
              name="mobilen_region"
              options={languages}
              itemTemplate={countryOptionTemplate}
              valueTemplate={selectedCountryTemplate}
              data-cy="mobilen_region"
            />

            <Field
              as={InputText}
              id="mobilen"
              name="mobilen"
              maxLength="13"
              value={formatMobileNumber(
                values.mobilen,
                values.mobilen_region ||
                  getPhoneorMobileNumberRegion(values.mobilen)
              )}
              onChange={(e) => {
                setFieldValue(
                  'mobilen',
                  e.target.value?.replace(invalidPhoneNumberCharactersRegex, '')
                );
              }}
              data-cy="mobile"
            />
          </div>
        </FieldWithErrors>

        <FieldWithErrors name="aktiven" label={false}>
          <div className="checkbox-container">
            <Field
              inputId="aktiven"
              name="aktiven"
              as={Checkbox}
              checked={values.aktiven}
            />

            <label htmlFor="aktiven">{t('Set status to active?')}</label>
          </div>
        </FieldWithErrors>
      </div>

      {!isEditDialog && (
        <div className="form-divider">
          <FieldWithErrors name="username" label={t('Email (username)')}>
            <Field
              as={InputText}
              id="username"
              name="username"
              maxLength="254"
              data-cy="username"
            />
          </FieldWithErrors>

          <FieldWithErrors name="datum_raganje" label={t('Date of Birth')}>
            <Field
              name="datum_raganje"
              as={Calendar}
              inputId="datum_raganje"
              dateFormat="dd/mm/yy"
              monthNavigator
              yearNavigator
              yearRange={`1900:${dayjs().format('YYYY')}`}
              inputClassName="data-cy-dob"
              onChange={(e) =>
                setFieldValue('datum_raganje', e.value ?? '', false)
              }
            />
          </FieldWithErrors>

          <FieldWithErrors name="embg" label={t('Personal No.')}>
            <Field
              as={InputText}
              id="embg"
              name="embg"
              maxLength="13"
              data-cy="ssn"
            />
          </FieldWithErrors>

          <FieldWithErrors name="password" label={t('Password')}>
            <Field
              as={InputText}
              id="password"
              name="password"
              data-cy="password"
            />
          </FieldWithErrors>
        </div>
      )}

      <FieldWithErrors name="komentar" label={t('Note')}>
        <Field
          as={InputTextarea}
          autoResize
          id="komentar"
          name="komentar"
          data-cy="note"
        />
      </FieldWithErrors>
    </div>
  );
}

export default CreateEditDialogBasicInfoTab;
